import React, { useEffect, useState } from "react";
import { getNotionMeetings } from "../../businessModeService";
import {
  NOTION_MEETINGS_EXPLANATION,
  describeNotionMeeting,
  describeNotionStatus,
  emptyMeetingsText,
  windowLabel,
} from "./notionMeetings";

/**
 * Read-only list of the Notion AI meeting recordings the connector found inside
 * its "Syncs the last N days" window, each with its status or the reason it was
 * skipped. Nothing is chosen here: connecting Notion is the whole setup.
 *
 * @example
 * <NotionMeetingsPanel workflowId="629b15c8-..." />
 */
const NotionMeetingsPanel = ({ workflowId }) => {
  const [data, setData] = useState({ status: { state: "pending" }, meetings: [], windowDays: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const result = await getNotionMeetings(workflowId);
        if (!cancelled && result) setData(result);
      } catch (err) {
        if (!cancelled) setError(err?.message || "Couldn't load your Notion recordings.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [workflowId]);

  const notice = describeNotionStatus(data.status);

  return (
    <div className="ds-notion-meetings">
      <h4 className="ds-notion-meetings__heading">
        Meeting recordings{windowLabel(data.windowDays) ? ` (${windowLabel(data.windowDays)})` : ""}
      </h4>
      <p className="ds-notion-meetings__hint">{NOTION_MEETINGS_EXPLANATION}</p>

      {loading ? (
        <p className="ds-notion-meetings__muted">Loading…</p>
      ) : error ? (
        <p className="ds-notion-meetings__status ds-notion-meetings__status--error">{error}</p>
      ) : notice ? (
        <p
          className={`ds-notion-meetings__status ds-notion-meetings__status--${notice.tone}`}
          role="status"
        >
          {notice.text}
        </p>
      ) : data.meetings.length === 0 ? (
        <p className="ds-notion-meetings__muted">{emptyMeetingsText(data.status, data.windowDays)}</p>
      ) : (
        <ul className="ds-notion-meetings__list">
          {data.meetings.map((meeting) => {
            const view = describeNotionMeeting(meeting);
            return (
              <li key={meeting.id} className="ds-notion-meetings__row">
                <span className="ds-notion-meetings__date">{view.dateLabel}</span>
                <div className="ds-notion-meetings__row-main">
                  {meeting.url ? (
                    <a href={meeting.url} target="_blank" rel="noreferrer" className="ds-notion-meetings__title">
                      {view.title}
                    </a>
                  ) : (
                    <span className="ds-notion-meetings__title">{view.title}</span>
                  )}
                  <span className={`ds-notion-meetings__status ds-notion-meetings__status--${view.tone}`}>
                    {view.label}
                  </span>
                  {view.detail ? (
                    <span className="ds-notion-meetings__detail" title={view.detail}>
                      {view.detail}
                    </span>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default NotionMeetingsPanel;
